import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import {
  ChevronIcon,
  ClearButton,
  Container,
  EmptyState,
  LoadingState,
  OptionItem,
  Panel,
  SearchInput,
  Trigger,
  TriggerActions
} from './styles';

export type SearchableSelectOption = {
  value: string;
  label: string;
  description?: string;
  searchText?: string;
};

export type SearchableSelectProps = {
  options: SearchableSelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  fluid?: boolean;
  error?: boolean;
  openUpward?: boolean;
  noResultsMessage?: string;
  clearable?: boolean;
  filterOptions?: (
    options: SearchableSelectOption[],
    query: string
  ) => SearchableSelectOption[];
  renderOption?: (option: SearchableSelectOption, query: string) => ReactNode;
  formatSelectedLabel?: (option: SearchableSelectOption | undefined) => string;
};

const defaultFilter = (options: SearchableSelectOption[], query: string) => {
  const search = query.trim().toLowerCase();
  if (!search) return options;

  return options.filter(option => {
    const searchText = (option.searchText || `${option.label} ${option.description || ''}`)
      .toLowerCase();
    return searchText.includes(search);
  });
};

const defaultFormatSelectedLabel = (option: SearchableSelectOption | undefined) => {
  if (!option) return '';
  if (option.description) return `${option.label} · ${option.description}`;
  return option.label;
};

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value = '',
  onChange,
  onBlur,
  placeholder = 'Selecione...',
  loading = false,
  disabled = false,
  fluid = false,
  error = false,
  openUpward = true,
  noResultsMessage = 'Nenhum resultado encontrado',
  clearable = true,
  filterOptions = defaultFilter,
  renderOption,
  formatSelectedLabel = defaultFormatSelectedLabel
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const selectedOption = options.find(option => option.value === value);
  const filteredOptions = filterOptions(options, searchQuery);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(0);
  }, []);

  const openPanel = useCallback(
    (initialQuery = '') => {
      if (disabled) return;
      setIsOpen(true);
      setSearchQuery(initialQuery);
      setHighlightedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [disabled]
  );

  const selectOption = useCallback(
    (option: SearchableSelectOption) => {
      onChange?.(option.value);
      close();
    },
    [close, onChange]
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        close();
        onBlur?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [close, isOpen, onBlur]);

  useEffect(() => {
    if (highlightedIndex >= filteredOptions.length) {
      setHighlightedIndex(Math.max(0, filteredOptions.length - 1));
    }
  }, [filteredOptions.length, highlightedIndex]);

  const handleInputChange = (nextValue: string) => {
    if (!isOpen) {
      setIsOpen(true);
    }
    setSearchQuery(nextValue);
    setHighlightedIndex(0);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (!isOpen && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      event.preventDefault();
      openPanel(selectedOption?.label || '');
      return;
    }

    if (!isOpen) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const option = filteredOptions[highlightedIndex];
      if (option) selectOption(option);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      onBlur?.();
    }
  };

  const displayValue = isOpen
    ? searchQuery
    : selectedOption
    ? formatSelectedLabel(selectedOption)
    : '';

  const defaultRenderOption = (option: SearchableSelectOption) => (
    <>
      <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>{option.label}</div>
      {option.description && (
        <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>{option.description}</div>
      )}
    </>
  );

  return (
    <Container ref={containerRef} fluid={fluid}>
      <Trigger
        error={error}
        disabled={disabled}
        isOpen={isOpen}
        onMouseDown={event => {
          if (disabled) return;
          if ((event.target as HTMLElement).closest('button')) return;
          event.preventDefault();
          if (!isOpen) {
            openPanel('');
          } else {
            inputRef.current?.focus();
          }
        }}
      >
        <SearchInput
          ref={inputRef}
          value={displayValue}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          onChange={event => handleInputChange(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (!isOpen) onBlur?.();
          }}
        />
        <TriggerActions>
          {clearable && value && !disabled && (
            <ClearButton
              type="button"
              tabIndex={-1}
              onMouseDown={event => event.preventDefault()}
              onClick={event => {
                event.stopPropagation();
                onChange?.('');
                setSearchQuery('');
                close();
                onBlur?.();
              }}
              aria-label="Limpar seleção"
            >
              ×
            </ClearButton>
          )}
          <ChevronIcon isOpen={isOpen} />
        </TriggerActions>
      </Trigger>

      {isOpen && (
        <Panel openUpward={openUpward}>
          {loading ? (
            <LoadingState>Carregando...</LoadingState>
          ) : filteredOptions.length === 0 ? (
            <EmptyState>{noResultsMessage}</EmptyState>
          ) : (
            filteredOptions.map((option, index) => (
              <OptionItem
                key={option.value}
                isHighlighted={index === highlightedIndex}
                isSelected={option.value === value}
                onMouseDown={event => event.preventDefault()}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => selectOption(option)}
              >
                {renderOption ? renderOption(option, searchQuery) : defaultRenderOption(option)}
              </OptionItem>
            ))
          )}
        </Panel>
      )}
    </Container>
  );
};

export default SearchableSelect;
